/**
 * The Quality overview's section in view (Overview, Release gate, Does it work?, Is it safe?, Can I prove it?) and a way
 * to go to one: the section picker in the tab's title (QualitySectionCrumb) and, outside the workspace, the row of links
 * (QualitySectionMenu) share it. While the panel scrolls, the first section in its top half is the current one
 * (IntersectionObserver, rooted on the panel); `go` scrolls the panel only (scrollInPanel), smoothly unless the person
 * prefers reduced motion, and moves the focus to the section (keyboards and screen readers).
 */
import { useEffect, useRef, useState } from 'react';
import { SECTIONS } from './qualityModel';
import { scrollInPanel } from './QualityCommon';

export const SCROLLED = SECTIONS.filter((s) => !s.view);
// After a pick, the smooth scroll passes over the sections in between: they become current only once it is over.
const PICK_HOLD_MS = 900;

const prefersReducedMotion = () => {
  try { return Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches); } catch { return false; }
};

/** `watch`: anything that changes when the sections may have changed (the overview): they are looked for again. */
export default function useQualitySection(watch = null) {
  const [current, setCurrent] = useState(SCROLLED[0].key);
  const visible = useRef({});
  const heldUntil = useRef(0);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.IntersectionObserver !== 'function') { return undefined; }
    const elements = SCROLLED.map((s) => document.getElementById(s.id)).filter(Boolean);
    const root = elements[0]?.closest?.('.quality-panel') || null;
    const observer = new window.IntersectionObserver((entries) => {
      entries.forEach((entry) => { visible.current[entry.target.id] = entry.isIntersecting; });
      if (Date.now() < heldUntil.current) { return; }
      const first = SCROLLED.find((s) => visible.current[s.id]);
      if (first) { setCurrent(first.key); }
    }, { root, rootMargin: '0px 0px -55% 0px', threshold: [0, 0.01] });
    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [watch]);

  /** Scrolls the panel to the section `key` (a scrolled one) and makes it current; false when it is not on the page. */
  const go = (key) => {
    const section = SCROLLED.find((s) => s.key === key);
    const element = section && document.getElementById(section.id);
    if (!element) { return false; }
    heldUntil.current = Date.now() + PICK_HOLD_MS;
    setCurrent(section.key);
    scrollInPanel(element, { smooth: !prefersReducedMotion() });
    if (element.hasAttribute('tabindex')) { element.focus?.({ preventScroll: true }); }
    return true;
  };

  return { current, go };
}
